<template>
  <div
    class="banner-carousel relative aspect-video w-full cursor-grab touch-pan-y select-none overflow-hidden bg-gray-50 active:cursor-grabbing desk:aspect-auto"
    :class="{ 'banner-carousel-pair': pairMode }"
    @touchstart="startDrag"
    @touchmove="moveDrag"
    @touchend="endDrag"
    @touchcancel="cancelDrag"
    @mousedown="startMouseDrag"
    @dragstart.prevent
  >
    <div
      class="banner-track flex h-full w-full will-change-transform"
      :class="transitionClass"
      :style="trackStyle"
      @transitionend="handleTrackTransitionEnd"
    >
      <component
        v-for="slide in sliderItems"
        :key="slide.key"
        :is="slide.banner.url ? 'a' : 'div'"
        :href="slide.banner.url || null"
        :data-track-index="slide.trackIndex"
        :data-banner-index="slide.realIndex"
        :tabindex="pairMode && slide.trackIndex !== trackIndex && slide.trackIndex !== trackIndex + 1 ? -1 : undefined"
        class="banner-slide relative block h-full w-full min-w-full flex-none"
        @click="handleClick($event, slide.banner)"
      >
        <video
          v-if="isVideo(slide.banner)"
          :src="getMediaUrl(slide.banner)"
          :poster="getPosterUrl(slide.banner)"
          class="h-full w-full object-contain bg-gray-50"
          muted
          playsinline
          webkit-playsinline
          preload="auto"
          @ended="handleMediaEnded(slide.realIndex)"
          @error="handleMediaIssue(slide.realIndex)"
          @stalled="handleMediaIssue(slide.realIndex)"
          @loadeddata="handleVideoLoaded(slide.realIndex)"
        />
        <img
          v-else
          :src="getMediaUrl(slide.banner)"
          class="h-full w-full object-contain bg-gray-50"
          :alt="slide.banner.name || slide.banner.img || 'banner'"
          draggable="false"
          @error="handleImageError($event, slide.banner)"
        />
      </component>
    </div>

    <div
      v-if="banners.length > 1"
      class="absolute bottom-2 left-0 right-0 flex justify-center gap-1"
    >
      <button
        v-for="(banner, index) in banners"
        :key="banner.id || index"
        type="button"
        class="h-1.5 rounded-full transition-all"
        :class="index === activeIndex ? 'w-5 bg-primary' : 'w-1.5 bg-gray-300'"
        :aria-current="index === activeIndex ? 'true' : undefined"
        :aria-label="`切換至第 ${index + 1} 張 banner`"
        @click="select(index)"
      />
    </div>
    <div v-if="pairMode" class="desktop-carousel-controls" @mousedown.stop>
      <button type="button" aria-label="上一張活動" @click="previous">‹</button>
      <button type="button" :aria-pressed="desktopPaused" @click="toggleDesktopPlayback">
        {{ desktopPaused ? '播放活動' : '暫停輪播' }}
      </button>
      <button type="button" aria-label="下一張活動" @click="advance">›</button>
    </div>
  </div>
</template>

<script>
import desktop from '@/mixins/desktop';
import { imageFallbackSource } from '@/utils/desktopImages';
import {
  trackStep,
  toTrackIndex as bannerTrackIndex,
  pairPeekTrackIndex,
  nextSlide,
  previousSlide,
  finishTrack,
} from '@/utils/desktopBannerTrack';

const IMAGE_DELAY = 3000;

export default {
  mixins: [desktop],
  props: {
    banners: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      activeIndex: 0,
      trackIndex: 0,
      timer: null,
      mountedReady: false,
      dragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dragOffset: 0,
      suppressClick: false,
      snapTransition: false,
      transitioning: false,
      transitionFallback: null,
      desktopPaused: false,
    };
  },
  computed: {
    pairMode() {
      return this.isDesktop && this.banners.length > 1;
    },
    activeBanner() {
      return this.banners[this.activeIndex] || null;
    },
    sliderItems() {
      const slides = this.banners.map((banner, index) => ({
        banner,
        realIndex: index,
        trackIndex: this.toTrackIndex(index),
        key: `real-${banner.id || index}`,
      }));

      if (this.banners.length <= 1) {
        return slides;
      }

      const first = slides[0];

      if (this.pairMode) {
        return [
          ...slides,
          {
            banner: first.banner,
            realIndex: first.realIndex,
            trackIndex: pairPeekTrackIndex(this.banners.length),
            key: `pair-peek-${first.banner.id || first.realIndex}`,
          },
        ];
      }

      const last = slides[slides.length - 1];

      return [
        {
          banner: last.banner,
          realIndex: last.realIndex,
          trackIndex: 0,
          key: `clone-last-${last.banner.id || last.realIndex}`,
        },
        ...slides,
        {
          banner: first.banner,
          realIndex: first.realIndex,
          trackIndex: this.banners.length + 1,
          key: `clone-first-${first.banner.id || first.realIndex}`,
        },
      ];
    },
    trackStyle() {
      const step = trackStep(this.pairMode);
      return {
        transform: `translate3d(calc(${-this.trackIndex * step}% + ${this.dragOffset}px), 0, 0)`,
      };
    },
    transitionClass() {
      return this.dragging || this.snapTransition
        ? "transition-none"
        : "transition-transform duration-500 ease-out";
    },
  },
  watch: {
    isDesktop() {
      if (!this.mountedReady) return;
      this.trackIndex = this.toTrackIndex(this.activeIndex);
      this.scheduleActive();
    },
    banners: {
      immediate: true,
      handler() {
        this.clearTransitionFallback();
        this.transitioning = false;
        this.snapTransition = false;
        this.dragging = false;
        this.dragOffset = 0;

        if (this.activeIndex >= this.banners.length) {
          this.activeIndex = 0;
        }

        this.trackIndex = this.toTrackIndex(this.activeIndex);

        if (this.mountedReady) {
          this.scheduleActive();
        }
      },
    },
    activeIndex() {
      if (this.mountedReady && !this.transitioning) {
        this.scheduleActive();
      }
    },
  },
  mounted() {
    this.mountedReady = true;
    this.trackIndex = this.toTrackIndex(this.activeIndex);
    this.scheduleActive();
  },
  beforeDestroy() {
    this.clearTimer();
    this.clearTransitionFallback();
    this.removeMouseDragListeners();
  },
  methods: {
    encodeMediaUrl(url) {
      if (!url || typeof url !== "string") return url || "";
      try {
        // Encode path segments so Chinese / reserved chars (& etc.) work in <img>/<video src>
        const parsed = new URL(url, typeof window !== "undefined" ? window.location.origin : "https://game-853.com");
        parsed.pathname = parsed.pathname
          .split("/")
          .map((segment) => {
            if (!segment) return segment;
            try {
              return encodeURIComponent(decodeURIComponent(segment));
            } catch (e) {
              return encodeURIComponent(segment);
            }
          })
          .join("/");
        return parsed.toString();
      } catch (e) {
        return url;
      }
    },
    getMediaUrl(banner) {
      return this.encodeMediaUrl(banner.media_url || banner.img || "");
    },
    getPosterUrl(banner) {
      const poster = banner.prd_logo || banner.poster || "";
      return poster ? this.encodeMediaUrl(poster) : undefined;
    },
    isVideo(banner) {
      if (!banner) return false;

      if (banner.media_type === "video") {
        return true;
      }

      if (banner.mime_type && banner.mime_type.indexOf("video/") === 0) {
        return true;
      }

      return /\.mp4($|\?)/i.test(banner.media_url || banner.img || "");
    },
    toTrackIndex(index) {
      return bannerTrackIndex(index, this.banners.length, this.pairMode);
    },
    handleClick(event, banner) {
      if (this.suppressClick || !banner || !banner.url) {
        event.preventDefault();
      }
    },
    select(index) {
      if (index === this.activeIndex) {
        this.scheduleActive();
        return;
      }

      this.clearTimer();
      this.clearTransitionFallback();
      this.transitioning = false;
      this.snapTransition = false;
      this.dragging = false;
      this.dragOffset = 0;
      this.snapTransition = true;
      this.activeIndex = index;
      this.snapToTrackIndex(this.toTrackIndex(index), this.scheduleActive);
    },
    scheduleActive() {
      this.clearTimer();

      if (this.isDesktop && this.desktopPaused) return;
      if (this.dragging || this.transitioning || this.snapTransition) return;
      if (!this.activeBanner) return;

      if (this.isVideo(this.activeBanner)) {
        this.$nextTick(this.playVideo);

        const duration = Number(this.activeBanner.duration_seconds);
        // Always schedule an advance for videos so a failed play() cannot freeze the carousel.
        const waitMs =
          Number.isFinite(duration) && duration > 0
            ? duration * 1000 + 1000
            : IMAGE_DELAY;
        this.timer = setTimeout(this.advance, waitMs);

        return;
      }

      if (this.banners.length > 1) {
        this.timer = setTimeout(this.advance, IMAGE_DELAY);
      }
    },
    playVideo() {
      this.pauseAllVideos();
      if (this.isDesktop && this.desktopPaused) return;

      const video = this.getActiveVideo();
      if (!video) {
        this.advanceAfterDelay();
        return;
      }

      try {
        video.currentTime = 0;
      } catch (e) {
        // Some browsers reject currentTime changes before metadata is ready.
      }

      const play = video.play && video.play();
      if (play && play.catch) {
        play.catch(() => {
          // Keep the poster/frame visible; still advance via scheduleActive timer.
        });
      }
    },
    toggleDesktopPlayback() {
      this.desktopPaused = !this.desktopPaused;
      if (this.desktopPaused) this.pauseAllVideos();
      this.scheduleActive();
    },
    handleVideoLoaded(realIndex) {
      if (realIndex === this.activeIndex && !this.dragging && !this.transitioning) {
        this.playVideo();
      }
    },
    handleImageError(event, banner) {
      const poster = banner && banner.prd_logo;
      const fallback = event && event.target
        ? imageFallbackSource(event.target.src, poster, null)
        : null;
      if (fallback) {
        event.target.src = this.encodeMediaUrl(fallback);
        return;
      }
      if (event && event.target) {
        event.target.style.visibility = "hidden";
      }
    },
    advanceAfterDelay() {
      this.clearTimer();
      if (this.isDesktop && this.desktopPaused) return;
      this.timer = setTimeout(this.advance, IMAGE_DELAY);
    },
    handleMediaEnded(realIndex) {
      if (this.isDesktop && this.desktopPaused) return;
      if (realIndex === this.activeIndex) {
        this.advance();
      }
    },
    handleMediaIssue(realIndex) {
      if (realIndex === this.activeIndex) {
        this.advanceAfterDelay();
      }
    },
    advance() {
      this.clearTimer();

      if (this.banners.length <= 1) {
        this.scheduleActive();
        return;
      }

      if (this.transitioning) return;

      this.applySlideMove(nextSlide(this.activeIndex, this.banners.length, this.pairMode));
    },
    previous() {
      this.clearTimer();

      if (this.banners.length <= 1) {
        this.scheduleActive();
        return;
      }

      if (this.transitioning) return;

      this.applySlideMove(previousSlide(this.activeIndex, this.banners.length, this.pairMode));
    },
    handleTrackTransitionEnd(event) {
      if (event.target !== event.currentTarget || this.banners.length <= 1) return;
      if (!this.transitioning) return;

      this.finishSlideTransition();
    },
    applySlideMove(move) {
      if (!move || move.kind === 'idle') {
        this.scheduleActive();
        return;
      }
      if (move.kind === 'snap') {
        this.activeIndex = move.activeIndex;
        this.snapToTrackIndex(move.trackIndex, this.scheduleActive);
        return;
      }
      this.startSlideTransition(move.activeIndex, move.trackIndex);
    },
    startSlideTransition(activeIndex, trackIndex) {
      this.clearTransitionFallback();
      this.transitioning = true;
      this.activeIndex = activeIndex;
      this.trackIndex = trackIndex;
      this.transitionFallback = setTimeout(this.finishSlideTransition, 650);
    },
    finishSlideTransition() {
      this.clearTransitionFallback();

      const result = finishTrack(
        this.trackIndex,
        this.activeIndex,
        this.banners.length,
        this.pairMode,
      );

      if (result.kind === 'snap') {
        this.snapToTrackIndex(result.trackIndex, this.scheduleActive);
        this.transitioning = false;
        return;
      }

      this.transitioning = false;
      this.trackIndex = result.trackIndex;
      this.scheduleActive();
    },
    snapToTrackIndex(index, callback) {
      this.snapTransition = true;
      this.trackIndex = index;

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.snapTransition = false;
            if (callback) {
              callback();
            }
          });
        });
      });
    },
    clearTransitionFallback() {
      if (!this.transitionFallback) return;
      clearTimeout(this.transitionFallback);
      this.transitionFallback = null;
    },
    clearTimer() {
      if (!this.timer) return;
      clearTimeout(this.timer);
      this.timer = null;
    },
    getActiveVideo() {
      const slide = this.$el.querySelector(`[data-track-index="${this.trackIndex}"]`);

      return slide ? slide.querySelector("video") : null;
    },
    pauseAllVideos() {
      this.$el.querySelectorAll("video").forEach((video) => {
        video.pause();
      });
    },
    startDrag(event) {
      if (
        this.banners.length <= 1 ||
        this.transitioning ||
        this.snapTransition ||
        !event.touches ||
        !event.touches.length
      ) {
        return;
      }

      this.beginDrag(event.touches[0].clientX, event.touches[0].clientY);
    },
    moveDrag(event) {
      if (!this.dragging || !event.touches || !event.touches.length) return;

      this.updateDrag(event.touches[0].clientX, event.touches[0].clientY);
    },
    startMouseDrag(event) {
      if (
        this.banners.length <= 1 ||
        this.transitioning ||
        this.snapTransition ||
        event.button !== 0
      ) {
        return;
      }

      event.preventDefault();
      this.beginDrag(event.clientX, event.clientY);
      window.addEventListener("mousemove", this.moveMouseDrag);
      window.addEventListener("mouseup", this.endMouseDrag);
    },
    moveMouseDrag(event) {
      if (!this.dragging) return;

      event.preventDefault();
      this.updateDrag(event.clientX, event.clientY);
    },
    endMouseDrag() {
      this.removeMouseDragListeners();
      this.endDrag();
    },
    removeMouseDragListeners() {
      window.removeEventListener("mousemove", this.moveMouseDrag);
      window.removeEventListener("mouseup", this.endMouseDrag);
    },
    beginDrag(clientX, clientY) {
      this.clearTimer();
      this.dragging = true;
      this.dragStartX = clientX;
      this.dragStartY = clientY;
      this.dragOffset = 0;
    },
    updateDrag(clientX, clientY) {
      const offsetX = clientX - this.dragStartX;
      const offsetY = clientY - this.dragStartY;

      if (Math.abs(offsetY) > Math.abs(offsetX) && Math.abs(offsetY) > 12) {
        this.dragOffset = 0;
        return;
      }

      this.dragOffset = offsetX;
    },
    endDrag() {
      if (!this.dragging) return;

      const width = this.$el.clientWidth || 1;
      const threshold = Math.min(80, width * 0.18);
      const offset = this.dragOffset;
      const shouldChange = Math.abs(offset) >= threshold;

      this.dragging = false;
      this.dragOffset = 0;

      if (Math.abs(offset) > 8) {
        this.suppressClick = true;
        setTimeout(() => {
          this.suppressClick = false;
        }, 300);
      }

      if (!shouldChange) {
        this.scheduleActive();
        return;
      }

      if (offset < 0) {
        this.advance();
      } else {
        this.previous();
      }
    },
    cancelDrag() {
      if (!this.dragging) return;

      this.removeMouseDragListeners();
      this.dragging = false;
      this.dragOffset = 0;
      this.scheduleActive();
    },
  },
};
</script>

<style scoped>
.desktop-carousel-controls {
  display: none;
}

.banner-carousel-pair .banner-track {
  height: 196px !important;
}
.banner-carousel-pair .banner-slide {
  width: 50% !important;
  min-width: 50% !important;
  padding: 0 8px;
}
.banner-carousel-pair .desktop-carousel-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 44px;
  color: var(--color-primary);
  font-size: 13px;
}

@media (min-width: 1024px) {
  .banner-carousel {
    height: 240px !important;
    aspect-ratio: auto !important;
    background: white !important;
  }
  .banner-slide img,
  .banner-slide video {
    border-radius: 8px;
  }
  .banner-carousel > .absolute {
    bottom: 10px !important;
    padding: 0 140px;
    pointer-events: none;
  }
  .banner-carousel > .absolute button {
    position: relative;
    pointer-events: auto;
  }
  .banner-carousel > .absolute button::after {
    content: '';
    position: absolute;
    inset: -9px -2px;
  }
  .desktop-carousel-controls button {
    min-width: 44px;
    min-height: 36px;
    border-radius: 4px;
  }
  .desktop-carousel-controls button:nth-child(2) {
    margin-left: auto !important;
    padding: 0 12px !important;
  }
  .desktop-carousel-controls button:hover {
    background: #f5f5f5;
  }
}
</style>
